/**
 * Grading scales per МОН order №722 (04.05.2026) and order №329 (12-point scale, grades 10–11).
 * Codes only — human-readable Ukrainian labels live in @shkola/i18n.
 */

export const SCALE_CODES = ['verbal', 'levels4', 'points12', 'pass_fail'] as const;
export type ScaleCode = (typeof SCALE_CODES)[number];

/** Level scale: П (початковий), С (середній), Д (достатній), В (високий). */
export const LEVELS = ['П', 'С', 'Д', 'В'] as const;
export type Level = (typeof LEVELS)[number];

/** Correspondence between levels and the 12-point scale (order №722). */
export const LEVEL_POINT_RANGES: Readonly<Record<Level, readonly [number, number]>> = {
  П: [1, 3],
  С: [4, 6],
  Д: [7, 9],
  В: [10, 12],
};

/**
 * Verbal scale codes, ordered from lowest to highest. Default wording follows МОН
 * recommendations for grades 1–4 (order №813); confirm with the methodologist.
 */
export const VERBAL_VALUES = [
  'needs_help', // «ще потребує допомоги»
  'with_teacher_help', // «досягає результату з допомогою вчителя»
  'notable_progress', // «демонструє помітний прогрес»
  'significant_success', // «має значні успіхи»
] as const;
export type VerbalValue = (typeof VERBAL_VALUES)[number];

export type GradeValue =
  | { scale: 'points12'; points: number }
  | { scale: 'levels4'; level: Level }
  | { scale: 'verbal'; verbal: VerbalValue }
  | { scale: 'pass_fail'; passed: boolean };

/** Marks that are not grades: «н/а» (не атестований) and «звільнений». */
export type SpecialMark = 'not_assessed' | 'exempt';

export function isValidGrade(value: GradeValue): boolean {
  switch (value.scale) {
    case 'points12':
      return Number.isInteger(value.points) && value.points >= 1 && value.points <= 12;
    case 'levels4':
      return (LEVELS as ReadonlyArray<string>).includes(value.level);
    case 'verbal':
      return (VERBAL_VALUES as ReadonlyArray<string>).includes(value.verbal);
    case 'pass_fail':
      return typeof value.passed === 'boolean';
  }
}

export function levelForPoints(points: number): Level {
  if (!Number.isInteger(points) || points < 1 || points > 12) {
    throw new RangeError(`points must be an integer 1..12, got ${points}`);
  }
  for (const level of LEVELS) {
    const [min, max] = LEVEL_POINT_RANGES[level];
    if (points >= min && points <= max) return level;
  }
  throw new RangeError(`no level for ${points}`);
}

export function levelOrdinal(level: Level): number {
  return LEVELS.indexOf(level) + 1;
}

export function levelFromOrdinal(ordinal: number): Level {
  const level = LEVELS[ordinal - 1];
  if (!level) throw new RangeError(`level ordinal must be 1..4, got ${ordinal}`);
  return level;
}
